import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { APIService, KPI } from '../api';
import { ScopeService } from './scope.service';

@Injectable()
export class UsersService {
  private _users$: Observable<KPI.User[]>;
  private _allUsers$: Observable<KPI.User[]>;
  private refresh$ = new BehaviorSubject<void>(null);

  private _membersGroups$: Observable<KPI.MembersGroup[]>;
  private _currentUserGroups$: Observable<KPI.MembersGroup[]>;
  private refreshMembersGroups$ = new BehaviorSubject<void>(null);

  private _mentions$: Observable<KPI.User[]>;

  constructor(private api: APIService, private scopeService: ScopeService, private translate: TranslateService) {}

  public get users$(): Observable<KPI.User[]> {
    if (!this._users$) {
      this._users$ = this.refresh$.pipe(
        switchMap(() => this.api.users_members(null, true)),
        shareReplay(1)
      );
    }
    return this._users$;
  }

  public get mentions$(): Observable<KPI.User[]> {
    if (!this._mentions$) {
      this._mentions$ = this.refresh$.pipe(
        switchMap(() => this.api.users_mentions()),
        shareReplay(1)
      );
    }
    return this._mentions$;
  }

  public get allUsers$(): Observable<KPI.User[]> {
    if (!this._allUsers$) {
      this._allUsers$ = this.refresh$.pipe(
        switchMap(() => this.api.users_members({ corporate: "all" }, true)),
        shareReplay(1)
      );
    }
    return this._allUsers$;
  }

  public createUser(user: KPI.UserInput): Observable<KPI.User> {
    return this.api.users_members_post(user).pipe(tap(() => this.refresh$.next()));
  }

  public updateUser(id: number, user: Partial<KPI.UserInput>): Observable<KPI.User> {
    return this.api.users_members_patch(id, user);
  }

  public deleteUser(userId: UserID): Observable<any> {
    return this.api.users_members_delete(userId).pipe(tap(() => this.refresh$.next()));
  }

  /**
   * Calls the api to recover the list of member groups
   */
  public get membersGroups$(): Observable<KPI.MembersGroup[]> {
    if (!this._membersGroups$) {
      this._membersGroups$ = this.refreshMembersGroups$.pipe(
        switchMap(() => this.api.users_members_groups(null, true)),
        shareReplay(1)
      );
    }
    return this._membersGroups$;
  }

  public getMembersGroup(id: number): Observable<KPI.MembersGroup | undefined> {
    return this._membersGroups$.pipe(map(groups => groups.find(g => g.id === id)));
  }

  public createMembersGroup(membersGroup: Partial<KPI.MembersGroup>): Observable<KPI.MembersGroup> {
    return this.api.post_members_group(membersGroup).pipe(tap(() => this.refreshMembersGroups$.next()));
  }

  public updateMembersGroup(id: number, memberGroup: Partial<KPI.MembersGroupInput>): Observable<KPI.MembersGroup> {
    return this.api.patch_members_group(id, memberGroup).pipe(tap(() => this.refreshMembersGroups$.next()));
  }

  public deleteMembersGroup(id: number): Observable<void> {
    return this.api.delete_members_group(id).pipe(tap(() => this.refreshMembersGroups$.next()));
  }

  /**
   * Get current user's groups
   */
  public get currentUserGroups$(): Observable<KPI.MembersGroup[]> {
    if (!this._currentUserGroups$) {
      this._currentUserGroups$ = this.refreshMembersGroups$.pipe(
        switchMap(() => this.scopeService.user$),
        switchMap(user => this.api.users_members_groups({ user: user?.id }, true)),
        shareReplay(1)
      );
    }
    return this._currentUserGroups$;
  }

  /**
   * Get current user's permissions as strings
   */
  public get currentUserGroupPermissions$(): Observable<KPI.AvailableGroupPermission[]> {
    return this.currentUserGroups$.pipe(
      map(groups => [
        ...new Set(
          groups.reduce((acc, group) => {
            acc.push(...group.permissions.map(p => p.setup_reference));
            return acc;
          }, [])
        )
      ])
    );
  }

  /**
   * Check if current user has the given permissions
   */
  public hasCurrentUserPermissions(permissions: KPI.AvailableGroupPermission[]): Observable<boolean> {
    return this.currentUserGroupPermissions$.pipe(
      map(currentPermissions => permissions.every(p => currentPermissions.includes(p)))
    );
  }

  /**
   * Check if current user has at least one permission among the given ones
   */
  public hasCurrentUserAtLeastOnePermission(permissions: KPI.AvailableGroupPermission[]): Observable<boolean> {
    return this.currentUserGroupPermissions$.pipe(
      map(currentPermissions => permissions.some(p => currentPermissions.includes(p)))
    );
  }

  public get hasScenarioManagementEnabled$(): Observable<boolean> {
    return this.currentUserGroups$.pipe(
      map(groups => groups.some(g => g.scenario_management_enabled === true))
    );
  }

  public get isCurrentUserSuperAdmin$(): Observable<boolean> {
    return this.currentUserGroups$.pipe(
      map(groups => groups.some(g => g.is_superadmin === true))
    );
  }

  public refreshUsers(): void {
    this.refresh$.next();
  }

  /**
   * Refresh the list of members group
   */
  public refreshMembersGroups(): void {
    this.refreshMembersGroups$.next();
  }

  public refreshCurrentUserGroups(): void {
    this._currentUserGroups$ = null;
    this.refreshMembersGroups$.next();
  }

  /**
   *
   */
  public getUserName(user: KPI.User): string {
    let userName = "";
    if (user) {
      if (user.first_name) {
        user.last_name ? (userName += `${user.first_name[0]}. `) : (userName += `${user.first_name}`);
      }

      if (user.last_name) {
        userName += `${user.last_name}`;
      }

      if (user.corporate_name) {
        if (user.first_name || user.last_name) {
          // if we have a user name, we put the corporate name in brackets
          userName += ` (${user.corporate_name})`;
        } else {
          userName += user.corporate_name;
        }
      }
    }

    if (userName === "") {
      userName = this.translate.instant("pages.common.charts.lifecycle-chart.unknown-user");
    }

    return userName;
  }

  private _userPerimeterPermissions$: { [userId: number]: Observable<KPI.PerimeterPermission[]> } = {};
  public getUserPerimeterPermissions(user: KPI.User): Observable<KPI.PerimeterPermission[]> {
    if (!this._userPerimeterPermissions$[user.id]) {
      this._userPerimeterPermissions$[user.id] = this.api
        .users_perimeter_permissions({
          user: user.get_user_id
        })
        .pipe(shareReplay(1));
    }
    return this._userPerimeterPermissions$[user.id];
  }

  public get currentUserPerimeterPermissions$(): Observable<KPI.PerimeterPermission[]> {
    return this.scopeService.user$.pipe(switchMap(user => this.getUserPerimeterPermissions(user)));
  }

  /** Sorry, I couldn't think of a shorter, more efficient name for the method... */
  public currentUserHasReadOnlyForSomePerimeters(siteIds: number[])
  : Observable<KPI.PerimeterPermission[]> {
    return this.currentUserPerimeterPermissions$.pipe(
      map(permissions =>
        permissions.filter(permission => siteIds.includes(permission.perimeter) && permission.read_only)
      )
    );
  }
}
